/**
 * Счета и кредиты со скриншотов банковского приложения (27.10.2026,
 * docs/design/bank-shots-2026-10-26).
 *
 * Сервер распознаёт экран и отдаёт только то, что на нём напечатано: суммы,
 * ставку и даты сверяет с текстом. Здесь найденное становится черновиком
 * счёта или кредита — тем же, что человек заполняет руками, — и называется
 * то, чего на экране не было: это человек проверит или доснимет.
 */
import type { PlainDate } from '@skoka/core';
import { formatAmountInput, todayPd } from './store.js';
import { api, unwrap } from './sync.js';
import { prepareReceipt } from './image.js';
import { BANKS } from './banks.js';
import { newAccount, type AccountDraft, type AccKind } from './account-form.js';
import { newLoan, type DLoan } from './debt-sheet.js';

export interface ShotAccount {
  title: string;
  kind: 'debit' | 'savings' | 'credit_card' | 'brokerage';
  last4: string | null;
  /** Копейки. У кредитки — сколько доступно, если напечатано. */
  balance: number | null;
  debt: number | null;
  limit: number | null;
  paymentDue: number | null;
  paymentDate: string | null;
}
export interface ShotLoan {
  title: string;
  kind: 'mortgage' | 'car' | 'consumer' | 'installment';
  principal: number | null;
  payoff: number | null;
  payment: number | null;
  nextPaymentDate: string | null;
  ratePct: number | null;
  endDate: string | null;
  openedAt: string | null;
  originalAmount: number | null;
  schedule: 'annuity' | 'differentiated' | null;
  paymentsLeft: number | null;
}
export interface ShotPage {
  bank: string | null; accounts: ShotAccount[]; loans: ShotLoan[]; dropped: number;
  /** Валютные счета: прочитались, но приложение считает в рублях. */
  foreign?: number;
}

/** Где разбор: в настройке — до пяти без Pro, в приложении — Pro. */
export type ShotMode = 'onboarding' | 'app';

export async function readBankShots(files: readonly File[], mode: ShotMode): Promise<ShotPage[]> {
  const pages = await Promise.all(files.map((f) => prepareReceipt(f)));
  const body = await unwrap(await api('/ai/parse-balances', {
    method: 'POST',
    body: JSON.stringify({ mode, today: todayPd(), pages: pages.map((p) => ({ base64: p.base64, mimeType: p.mimeType })) }),
  })) as { pages?: ShotPage[] };
  return Array.isArray(body.pages) ? body.pages : [];
}

/** Как банк называют на экране и в жизни → его значок в приложении. */
const ALIASES: [string, RegExp][] = [
  ['sber', /сбер|sber/i], ['tbank', /т-?банк|тинькофф|tinkoff|t-?bank/i], ['alfa', /альфа|alfa/i],
  ['vtb', /втб|vtb/i], ['ozon', /озон|ozon/i], ['wb', /вайлдберриз|wildberries|wb\s?банк/i],
  ['gpb', /газпромбанк|gazprombank/i], ['raif', /райффайзен|raiffeisen/i], ['sovcom', /совкомбанк|халва|sovcom/i],
  ['mts', /мтс|mts/i], ['psb', /псб|промсвязьбанк|psb/i], ['rshb', /россельхоз|rshb/i], ['otp', /отп|otp/i],
  ['mkb', /мкб|московский кредитный/i], ['bspb', /санкт-петербург|bspb/i], ['akbars', /ак барс|akbars/i],
  ['rosbank', /росбанк|rosbank/i], ['renins', /ренессанс|renaissance/i], ['tochka', /точка|tochka/i], ['modul', /модульбанк|modulbank/i],
];
export const bankIdOf = (...names: (string | null | undefined)[]): string | null => {
  const text = names.filter(Boolean).join(' ');
  return text ? ALIASES.find(([, re]) => re.test(text))?.[0] ?? null : null;
};
/** Коротко — для названия счёта: «Сбер · Накопительный». */
const SHORT: Record<string, string> = {
  sber: 'Сбер', tbank: 'Т-Банк', alfa: 'Альфа', vtb: 'ВТБ', ozon: 'Озон', wb: 'WB Банк', gpb: 'Газпромбанк',
  raif: 'Райффайзен', sovcom: 'Совкомбанк', mts: 'МТС Банк', psb: 'ПСБ', rshb: 'РСХБ', otp: 'ОТП', mkb: 'МКБ',
  bspb: 'БСПБ', akbars: 'Ак Барс', rosbank: 'Росбанк', renins: 'Ренессанс', tochka: 'Точка', modul: 'Модульбанк',
};
export const bankName = (id: string | null): string | null => (id ? BANKS.find((b) => b.id === id)?.name ?? null : null);

const rub = (k: number | null): string => (k === null ? '' : formatAmountInput(String(k / 100)));
const day = (date: string | null): string => (date ? String(Number(date.slice(8, 10))) : '');

/** Название без повтора банка: «СберКарта» не становится «Сбер · СберКарта». */
function nameOf(bankId: string | null, title: string, last4: string | null): string {
  const short = bankId ? SHORT[bankId] : null;
  const base = short && !title.toLowerCase().includes(short.toLowerCase().slice(0, 4)) ? `${short} · ${title}` : title;
  return last4 ? `${base} ··${last4}` : base;
}

/** Черновик счёта и что в нём проверить: на экране банка не видно, тратят ли с накопительного. */
export interface FoundAccount { draft: AccountDraft; warn: string | null; key: string }

export function shotAccount(a: ShotAccount, bankId: string | null): FoundAccount {
  const kind: AccKind = a.kind === 'brokerage' ? 'savings' : a.kind;
  const draft: AccountDraft = {
    ...newAccount(nameOf(bankId, a.title, a.last4), kind),
    ...(bankId ? { icon: `bank:${bankId}` } : {}),
  };
  let warn: string | null = null;
  if (kind === 'credit_card') {
    draft.balance = rub(a.debt ?? (a.limit !== null && a.balance !== null && a.limit >= a.balance ? a.limit - a.balance : null));
    draft.limit = rub(a.limit);
    draft.needGrace = rub(a.paymentDue);
    if (a.paymentDate) draft.payDay = day(a.paymentDate);
    else warn = 'до какого числа платёж?';
    if (!draft.balance) warn = 'долг не прочитался — впиши';
  } else {
    draft.balance = rub(a.balance);
    if (kind === 'savings') { draft.liquid = false; warn = 'проверь: тратишь отсюда или копишь?'; }
    if (a.kind === 'brokerage') warn = null;
  }
  return { draft, warn, key: `${bankId ?? ''}:${a.last4 ?? a.title.toLowerCase()}` };
}

/** Чего не хватает кредиту, — словами для плашки и строки. */
export function loanMissing(l: Pick<DLoan, 'principal' | 'payment' | 'payDate' | 'rate' | 'endDate' | 'openedAt'> & { scheduleKnown?: boolean }): string[] {
  const out: string[] = [];
  if (!l.principal) out.push('остатка');
  if (!l.payment) out.push('платежа');
  if (!l.payDate) out.push('даты платежа');
  if (!l.rate) out.push('ставки');
  if (!l.endDate) out.push('срока');
  if (!l.openedAt) out.push('даты выдачи');
  if (l.scheduleKnown === false) out.push('графика');
  return out;
}

/** «ставки, срока и даты выдачи». */
export const listRu = (xs: readonly string[]): string => (xs.length <= 1 ? xs.join('')
  : `${xs.slice(0, -1).join(', ')} и ${xs[xs.length - 1]}`);

export interface FoundLoan { draft: DLoan; scheduleKnown: boolean; bankId: string | null; kind: ShotLoan['kind'] }

export function shotLoan(l: ShotLoan, bankId: string | null, today: PlainDate = todayPd()): FoundLoan {
  const short = bankId ? SHORT[bankId] : null;
  const base = newLoan(short ? `${l.title} · ${short}` : l.title, l.kind === 'installment' ? 'installment' : 'amortizing');
  const draft: DLoan = {
    ...base,
    principal: rub(l.principal ?? l.payoff),
    // Только «к полному погашению» — это сумма уже с процентами.
    ...(l.principal === null && l.payoff !== null ? { withInterest: true } : {}),
    principalAsOf: today,
    payment: rub(l.payment),
    rate: l.ratePct === null ? '' : String(l.ratePct).replace('.', ','),
    endDate: l.endDate ?? '',
    openedAt: l.openedAt ?? '',
    schedule: l.schedule ?? 'annuity',
    left: l.paymentsLeft === null ? '' : String(l.paymentsLeft),
    ...(l.nextPaymentDate ? { payDate: l.nextPaymentDate, day: day(l.nextPaymentDate) } : { payDate: '' }),
  };
  return { draft, scheduleKnown: l.schedule !== null, bankId, kind: l.kind };
}

/**
 * Дописать кредит вторым скриншотом — экраном условий. Пустое заполняется,
 * «к полному погашению» уступает остатку основного долга; названное
 * человеком руками не трогаем.
 */
export function mergeLoan(into: FoundLoan, more: FoundLoan): FoundLoan {
  const d = { ...into.draft };
  const m = more.draft;
  if (m.principal && (!d.principal || (d.withInterest && !m.withInterest))) {
    d.principal = m.principal;
    if (m.withInterest) d.withInterest = true; else delete d.withInterest;
  }
  for (const k of ['payment', 'rate', 'endDate', 'openedAt', 'left'] as const) if (!d[k] && m[k]) d[k] = m[k];
  if (!d.payDate && m.payDate) { d.payDate = m.payDate; d.day = m.day; }
  if (more.scheduleKnown) d.schedule = m.schedule;
  // «Кредит» с экрана остатка становится ипотекой, если так сказал экран условий.
  const kind = into.kind === 'consumer' && more.kind !== 'consumer' ? more.kind : into.kind;
  if (kind !== into.kind) d.name = m.name;
  return { ...into, kind, draft: d, scheduleKnown: into.scheduleKnown || more.scheduleKnown, bankId: into.bankId ?? more.bankId };
}

/** Экран условий: ставка и сроки есть, остатка и платежа нет. */
export const termsOnly = (d: Pick<DLoan, 'principal' | 'payment'>): boolean => !d.principal && !d.payment;

/**
 * Тот же кредит на другом экране: тот же вид и банк — или единственный.
 * Экран условий дописывает кредит и при другом виде: на экране остатка у
 * Сбера нет слова «ипотека», и вид там читается как «кредит».
 */
export function sameLoan(a: FoundLoan, b: FoundLoan): boolean {
  return (a.kind === b.kind || termsOnly(a.draft) || termsOnly(b.draft))
    && (a.bankId === b.bankId || a.bankId === null || b.bankId === null);
}

/** Название для сравнения: без банка впереди, цифр карты, многоточия и регистра. */
function bare(title: string): string {
  return title.replace(/\s*··\d{4}$/, '').replace(/^[^·]+ · /, (m) => (Object.values(SHORT).includes(m.slice(0, -3)) ? '' : m))
    .replace(/(…|\.\.\.)$/, '').replace(/ё/g, 'е').replace(/\s+/g, ' ').trim().toLowerCase();
}
const last4Of = (name: string): string | null => /··(\d{4})$/.exec(name)?.[1] ?? null;

/**
 * Тот же счёт под двумя названиями: с главного экрана банка и из списка
 * счетов, где он назван целиком («Платежи (авто-кред…» и «Платежи
 * (авто-кредит, ипотека)»), или с банком впереди и без. Цифры карты решают
 * сразу: разные — разные счета.
 */
export function sameAccountName(a: string, b: string): boolean {
  const la = last4Of(a);
  const lb = last4Of(b);
  if (la && lb) return la === lb;
  const x = bare(a);
  const y = bare(b);
  if (x === y) return true;
  const [short, long] = x.length <= y.length ? [x, y] : [y, x];
  return short.length >= 4 && long.startsWith(short);
}

/** Один счёт с двух скриншотов: название длиннее, сумма с копейками точнее круглой. */
function mergeAccount(a: ShotAccount, b: ShotAccount): ShotAccount {
  const precise = (x: number | null, y: number | null) => (x === null ? y : y !== null && x % 100 === 0 && y % 100 !== 0 ? y : x);
  return {
    ...a,
    title: b.title.replace(/…$/, '').length > a.title.replace(/…$/, '').length ? b.title : a.title,
    last4: a.last4 ?? b.last4,
    balance: precise(a.balance, b.balance),
    debt: a.debt ?? b.debt, limit: a.limit ?? b.limit, paymentDue: a.paymentDue ?? b.paymentDue, paymentDate: a.paymentDate ?? b.paymentDate,
  };
}

/** Страницы → счета и кредиты без повторов: один счёт на двух скриншотах — один. */
export function collect(pages: readonly ShotPage[], today: PlainDate = todayPd()): {
  accounts: FoundAccount[]; loans: FoundLoan[]; dropped: number; foreign: number;
} {
  const raw: { a: ShotAccount; bankId: string | null }[] = [];
  const loans: FoundLoan[] = [];
  let dropped = 0;
  let foreign = 0;
  const label = (x: ShotAccount) => (x.last4 ? `${x.title} ··${x.last4}` : x.title);
  for (const p of pages) {
    dropped += p.dropped;
    foreign += p.foreign ?? 0;
    for (const a of p.accounts) {
      const bankId = bankIdOf(p.bank, a.title);
      const i = raw.findIndex((x) => (x.bankId === bankId || !x.bankId || !bankId) && sameAccountName(label(x.a), label(a)));
      if (i >= 0) raw[i] = { a: mergeAccount(raw[i]!.a, a), bankId: raw[i]!.bankId ?? bankId };
      else raw.push({ a, bankId });
    }
    for (const l of p.loans) {
      const found = shotLoan(l, bankIdOf(p.bank, l.title), today);
      const i = loans.findIndex((x) => sameLoan(x, found));
      if (i >= 0) loans[i] = mergeLoan(loans[i]!, found); else loans.push(found);
    }
  }
  return { accounts: raw.map(({ a, bankId }) => shotAccount(a, bankId)), loans, dropped, foreign };
}
